import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BACKEND_URL from '../../vnog/config';

interface AIOperatorFeedbackItem {
    id: number;
    event_id: number;
    rating: number;
    comment?: string;
    created_at: string;
    created_by_email?: string;
}

interface AIOperatorFeedbackDetail {
    id: number;
    rating: number;
    comment?: string;
    created_at: string;
    created_by?: {
        id: number;
        user: {
            email: string;
        };
    };
    simulation_data?: {
        id: number;
        token: string;
        created_at: string;
        status: string;
        address?: {
            id: number;
            street: string;
            zipcode: string;
            house_number: string;
        };
        event_log: Array<{
            id: number;
            order?: number;
            start_time?: string;
            end_time?: string;
            event_type: string;
            message_content?: string;
            message_sender?: string;
            other_arguments?: any;
        }>;
    };
    event: {
        id: number;
        order?: number;
        start_time?: string;
        end_time?: string;
        event_type: string;
        message_content?: string;
        message_sender?: string;
        other_arguments?: any;
    };
    last_seen_event?: {
        id: number;
        order?: number;
        start_time?: string;
        end_time?: string;
        event_type: string;
        message_content?: string;
        message_sender?: string;
        other_arguments?: any;
    };
}

interface AIOperatorMessagesProps {
    onLoadingChange?: (loading: boolean) => void;
}

const AIOperatorFeedbackDetail: React.FC<{ detail: AIOperatorFeedbackDetail | null }> = ({ detail }) => {
    if (!detail) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="text-gray-500 text-center">Select a feedback item to view details</div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">AI Operator Feedback Details</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column - Main details */}
                <div className="space-y-4">
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Rating</div>
                        <div className="text-lg font-medium">{detail.rating}/5</div>
                    </div>
                    
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Comment</div>
                        <div className="text-sm text-gray-700 break-words">{detail.comment || 'No comment provided'}</div>
                    </div>
                    
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created By</div>
                        <div className="text-sm text-gray-700">{detail.created_by?.user?.email || 'Unknown'}</div>
                    </div>
                    
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created At</div>
                        <div className="text-sm text-gray-700">{new Date(detail.created_at).toLocaleString()}</div>
                    </div>
                    
                    {detail.event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Event Details</div>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.event.event_type}</div>
                                {detail.event.message_content && (
                                    <div className="text-sm"><strong>Message:</strong> {detail.event.message_content}</div>
                                )}
                                {detail.event.message_sender && (
                                    <div className="text-sm"><strong>Sender:</strong> {detail.event.message_sender}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {detail.last_seen_event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Last Seen Event</div>
                            <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.last_seen_event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.last_seen_event.event_type}</div>
                                {detail.last_seen_event.message_content && (
                                    <div className="text-sm"><strong>Message:</strong> {detail.last_seen_event.message_content}</div>
                                )}
                                {detail.last_seen_event.message_sender && (
                                    <div className="text-sm"><strong>Sender:</strong> {detail.last_seen_event.message_sender}</div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {detail.simulation_data && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Simulation Details</div>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-sm"><strong>Simulation ID:</strong> {detail.simulation_data.id}</div>
                                <div className="text-sm"><strong>Status:</strong> {detail.simulation_data.status}</div>
                                <div className="text-sm"><strong>Created:</strong> {new Date(detail.simulation_data.created_at).toLocaleString()}</div>
                                {detail.simulation_data.address && (
                                    <div className="text-sm">
                                        <strong>Address:</strong> {detail.simulation_data.address.street} {detail.simulation_data.address.house_number}, {detail.simulation_data.address.zipcode}
                                    </div>
                                )}
                                <div className="text-sm"><strong>Events:</strong> {detail.simulation_data.event_log.length} events</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right column - Event log */}
                {detail.simulation_data && detail.simulation_data.event_log && detail.simulation_data.event_log.length > 0 && (
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Event Log</div>
                        <div className="bg-gray-50 p-3 rounded max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                                {detail.simulation_data.event_log.map((event, index) => {
                                    const isTargetEvent = event.id === detail.event.id;
                                    const isLastSeenEvent = detail.last_seen_event && event.id === detail.last_seen_event.id;
                                    return (
                                        <div 
                                            key={event.id} 
                                            className={`p-3 rounded border ${
                                                isTargetEvent 
                                                    ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-400' 
                                                    : isLastSeenEvent
                                                    ? 'bg-orange-100 border-orange-300 ring-2 ring-orange-400'
                                                    : 'bg-white border-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        isTargetEvent 
                                                            ? 'bg-blue-200 text-blue-800' 
                                                            : isLastSeenEvent
                                                            ? 'bg-orange-200 text-orange-800'
                                                            : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                        #{index + 1}
                                                    </span>
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        event.event_type === 'ai_operator' 
                                                            ? 'bg-green-100 text-green-800'
                                                            : event.event_type === 'message'
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {event.event_type}
                                                    </span>
                                                    {isTargetEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 font-medium">
                                                            Target Event
                                                        </span>
                                                    )}
                                                    {isLastSeenEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-orange-200 text-orange-800 font-medium">
                                                            Last Seen
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    ID: {event.id}
                                                </div>
                                            </div>
                                            
                                            {event.message_content && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Message:</div>
                                                    <div className="text-sm text-gray-700 break-words">{event.message_content}</div>
                                                </div>
                                            )}
                                            
                                            {event.message_sender && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Sender:</div>
                                                    <div className="text-sm text-gray-700">{event.message_sender}</div>
                                                </div>
                                            )}
                                            
                                            {event.other_arguments && Object.keys(event.other_arguments).length > 0 && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Additional Data:</div>
                                                    <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                                                        <pre className="whitespace-pre-wrap break-words">
                                                            {JSON.stringify(event.other_arguments, null, 2)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                {event.start_time && (
                                                    <div>Start: {new Date(event.start_time).toLocaleString()}</div>
                                                )}
                                                {event.end_time && (
                                                    <div>End: {new Date(event.end_time).toLocaleString()}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AIOperatorMessages: React.FC<AIOperatorMessagesProps> = ({ onLoadingChange }) => {
    const [aiOperatorFeedback, setAiOperatorFeedback] = useState<AIOperatorFeedbackItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState<number | null>(null);
    const [feedbackDetails, setFeedbackDetails] = useState<Map<number, AIOperatorFeedbackDetail>>(new Map());
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchAiOperatorFeedback = async () => {
        setLoading(true);
        onLoadingChange?.(true);
        
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/feedback/ai_operator/`, {
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setAiOperatorFeedback(data);
            } else {
                toast.error('Failed to fetch AI operator feedback');
                setAiOperatorFeedback([]);
            }
        } catch (error) {
            toast.error('Error fetching AI operator feedback');
            setAiOperatorFeedback([]);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    const fetchFeedbackDetail = async (feedbackId: number) => {
        // Check if we already have this detail cached
        if (feedbackDetails.has(feedbackId)) {
            setSelectedFeedbackId(feedbackId);
            return;
        }

        setLoadingDetail(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/feedback/ai_operator/${feedbackId}/`, {
                credentials: 'include',
            });

            if (response.ok) {
                const detail = await response.json();
                console.log(detail);
                setFeedbackDetails(prev => new Map(prev).set(feedbackId, detail));
                setSelectedFeedbackId(feedbackId);
            } else {
                toast.error('Failed to fetch feedback details');
            }
        } catch (error) {
            toast.error('Error fetching feedback details');
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        fetchAiOperatorFeedback();
    }, []);

    if (loading) {
        return (
            <div className="px-6 py-4 bg-white rounded-lg shadow">Loading…</div>
        );
    }

    if (aiOperatorFeedback.length === 0) {
        return (
            <div className="px-6 py-4 bg-white rounded-lg shadow text-gray-500">No data</div>
        );
    }

    const selectedDetail = selectedFeedbackId ? feedbackDetails.get(selectedFeedbackId) : null;
    console.log(selectedDetail);

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-1">AI Operator Feedback</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Left column - List of feedback items */}
                <div className="lg:col-span-1">
                    <div className="space-y-3">
                        {aiOperatorFeedback.map((item) => (
                            <div 
                                key={item.id} 
                                className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-colors ${
                                    selectedFeedbackId === item.id 
                                        ? 'ring-2 ring-blue-500 bg-blue-50' 
                                        : 'hover:bg-gray-50'
                                }`}
                                onClick={() => fetchFeedbackDetail(item.id)}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm text-gray-500">Event ID</div>
                                    <div className="text-sm font-medium text-gray-900">{item.event_id}</div>
                                </div>
                                <div className="text-sm text-gray-500 mb-1">Rating</div>
                                <div className="text-sm font-medium text-gray-900 mb-2">{item.rating}/5</div>
                                <div className="text-sm text-gray-500 mb-1">Comment</div>
                                <div className="text-sm text-gray-700 mb-2 break-words line-clamp-2">
                                    {item.comment || 'No comment'}
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div>By: {item.created_by_email || '-'}</div>
                                    <div>{new Date(item.created_at).toLocaleDateString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right column - Detail view */}
                <div className="lg:col-span-3">
                    {loadingDetail ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-center">Loading details...</div>
                        </div>
                    ) : (
                        <AIOperatorFeedbackDetail detail={selectedDetail || null} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AIOperatorMessages;
